"use client";

import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldCheck, MapPin, Award, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function LocalIntro() {
  const shouldReduceMotion = useReducedMotion();
  const trustBadges = [{
    title: "NASC Accredited",
    desc: "National Access & Scaffolding Confederation member standards."
  }, {
    title: "CHAS Approved",
    desc: "Verified health and safety compliance for site work."
  }, {
    title: "20+ Years Leadership",
    desc: "Director Pete Wood oversees every project delivery."
  }];
  return <section id="localintro" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          
          {/* Main Content Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline" className="gap-1.5 py-1 px-3 text-xs font-semibold uppercase tracking-wider">
                <MapPin className="size-3.5 text-primary" />
                <span>Beaconsfield & Buckinghamshire</span>
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-6 font-default">
              Commercial and residential scaffolding across Beaconsfield
            </h2> 

            <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p className="font-medium text-foreground">
                BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Beaconsfield and the surrounding Buckinghamshire area, including HP9.
              </p>
              <p>
                Whether managing careful access for period and heritage-style homes, discreet setups along affluent residential avenues, or structured scaffolds around occupied commercial premises, we deliver safe, compliant structures with minimal disruption.
              </p>
              <p>
                Led by Pete Wood with over 20 years of hands-on scaffolding experience, our team combines serious commercial capability with the personal accountability of a family-run business. Every project is erected in full accordance with strict NASC and CHAS safety standards, ensuring reliable protection for contractors, homeowners, and the general public.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                  <span>Request a Beaconsfield quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/HealthSafetyAccreditationsPage">
                  View safety accreditations
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Highlights Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.55,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-4">
            <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-card-foreground text-base">
                    Local Access Standards
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Tailored for Beaconsfield properties
                  </p>
                </div>
              </div>

              <ul className="space-y-4">
                {trustBadges.map((badge, idx) => <li key={badge.title} data-index={idx} className="flex items-start gap-3.5 p-3 rounded-lg bg-muted/40">
                    <div className="mt-0.5 rounded-full bg-primary/10 p-1 text-primary shrink-0">
                      <CheckCircle2 className="size-4" />
                    </div>
                    <div>
                      <span className="block text-sm font-semibold text-card-foreground">
                        {badge.title}
                      </span>
                      <span className="text-xs text-muted-foreground leading-normal">
                        {badge.desc}
                      </span>
                    </div>
                  </li>)}
              </ul>

              <div className="mt-6 pt-5 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                  <Award className="size-4 text-primary" />
                  NASC & CHAS Verified
                </span>
                <span>South East & Home Counties</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}